import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { canEditItems } from "@/lib/roles";
import { MagnifyingGlass, ArrowRight, CaretUp, CaretDown, PencilSimple } from "@phosphor-icons/react";

const COLUMNS = [
  { key: "business_product_id", label: "Product ID" },
  { key: "product_name", label: "Product Name" },
  { key: "vendor", label: "Vendor" },
  { key: "category", label: "Category" },
  { key: "pack_size", label: "Pack Size" },
];

export default function Items() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: "product_name", dir: "asc" });
  const canEdit = canEditItems(user);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (cat) params.set("category", cat);
      params.set("limit", "500");
      const { data } = await api.get(`/items?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, cat]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Distinct sorted categories from live data, blanks stripped, case-insensitive dedup.
  const categories = useMemo(() => {
    const seen = new Map();
    items.forEach((v) => {
      const c = String(v.category || "").trim();
      if (!c) return;
      const k = c.toLowerCase();
      if (!seen.has(k)) seen.set(k, c);
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const sorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      const va = (a[sort.key] || "").toString().toLowerCase();
      const vb = (b[sort.key] || "").toString().toLowerCase();
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [items, sort]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const SortIcon = ({ col }) => sort.key !== col ? null : (sort.dir === "asc" ? <CaretUp size={10} className="inline ml-1" weight="bold" /> : <CaretDown size={10} className="inline ml-1" weight="bold" />);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4">
        <div className="label-tech mb-1">Catalog</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Dry Goods Items</h1>
      </div>

      <div className="card-flat border-border p-3 mb-4 grid grid-cols-1 md:grid-cols-[1fr_220px] gap-2">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-3.5 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by Product ID or Product Name…"
            className="w-full h-11 pl-9 pr-3 border border-border bg-background rounded-sm outline-none focus:ring-2 focus:ring-primary text-sm" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)}
          className="h-11 px-2 border border-border bg-background rounded-sm text-sm">
          <option value="">All categories</option>
          {categories.map((c) => (<option key={c} value={c}>{c}</option>))}
        </select>
      </div>

      <div className="card-flat border-border overflow-hidden" data-testid="items-list">
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 border-b border-border sticky top-0 z-10">
              <tr className="text-left label-tech">
                {COLUMNS.map((c) => (
                  <th key={c.key} className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort(c.key)}>
                    {c.label}<SortIcon col={c.key} />
                  </th>
                ))}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.map((v) => (
                <tr key={v.variant_id} className="hover:bg-muted/40">
                  <td className="px-3 py-2.5 font-mono text-xs" data-testid={`item-business-pid-${v.variant_id}`}>
                    {v.business_product_id && String(v.business_product_id).trim()
                      ? v.business_product_id
                      : <span className="text-muted-foreground italic">Missing</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium truncate max-w-[42ch]">{v.product_name}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {v.vendor && String(v.vendor).trim()
                      ? <span className="truncate max-w-[20ch] inline-block align-middle">{v.vendor}</span>
                      : <span className="text-muted-foreground italic">Unassigned</span>}
                  </td>
                  <td className="px-3 py-2.5 text-xs">{v.category || <span className="text-muted-foreground italic">—</span>}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{v.pack_size || `${v.pack_type} · ${v.units_per_pack} ${v.unit_size}`.trim()}</td>
                  <td className="px-3 py-2.5 text-right">
                    <Link to={`/items/${v.variant_id}${canEdit ? "?edit=1" : ""}`}
                      data-testid={`item-open-${v.variant_id}`}
                      className="text-primary hover:underline text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1">
                      {canEdit ? (<><PencilSimple size={12} weight="bold" /> Edit</>) : (<>Detail <ArrowRight size={12} weight="bold" /></>)}
                    </Link>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && !loading && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground text-sm">No items match.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground text-xs font-mono">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
